import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import {
  IconGripVertical, IconX, IconMinimize, IconMaximize,
  IconArrowsDiagonal, IconArrowsDiagonalMinimize2
} from '@tabler/icons-react';
import classes from './FloatingPanel.module.css';

/**
 * Presentation mode.
 *
 *  - `floating`   — the historical draggable window.
 *  - `fullscreen` — fills the viewport **below the navbar**; the navbar stays
 *    visible and clickable. Dragging and the minimize-to-titlebar affordance are
 *    both meaningless here, so they are hidden.
 */
export type FloatingPanelMode = 'floating' | 'fullscreen';

export interface FloatingPanelProps {
  width?: number;
  isVisible?: boolean;
  children: React.ReactNode;
  title?: string;
  onClose?: () => void;
  className?: string;
  initialPosition?: { x: number; y: number };
  /** Defaults to `floating` so existing callers keep their behaviour. */
  mode?: FloatingPanelMode;
  /** Rendered only when provided — omit it and the panel has no mode control. */
  onModeChange?: ( mode: FloatingPanelMode ) => void;
  modeLabels?: { expand: string; collapse: string };
}

export function FloatingPanel({
  width = 320,
  isVisible = true,
  children,
  title = 'AI Assistant',
  onClose,
  className,
  initialPosition,
  mode = 'floating',
  onModeChange,
  modeLabels
}: FloatingPanelProps ) {
  const isFullscreen = mode === 'fullscreen';
  const panelRef = useRef<HTMLDivElement>( null );

  // panelSize sólo controla el ancho; mantenemos la misma lógica que antes
  const [ panelSize, setPanelSize ] = useState({ width: width, height: 'auto' });
  useEffect(() => setPanelSize({ width: width, height: 'auto' }), [width]);

  // Estados para arrastre
  const [ isDragging, setIsDragging ] = useState( false );
  const [ dragOffset, setDragOffset ] = useState({ x: 0, y: 0 });
  // panelPosition almacena la última posición “fija” del panel
  const [ panelPosition, setPanelPosition ] = useState( initialPosition || { x: 0, y: 0 });

  useEffect(() => {
    if ( initialPosition ) {
      setPanelPosition( initialPosition );
    }
  }, [initialPosition]);

  // Estado de “minimizado” (y en tu caso, “maximize” es simplemente `isMinimized = false`)
  const [ isMinimized, setIsMinimized ] = useState( false );
  const [ animateTitle, setAnimateTitle ] = useState( false );

  // -----------------------------
  // 1) Lógica de arrastre
  // -----------------------------

  // Cuando hago mousedown sobre el handle, calculo el offset e inicio dragging.
  // En fullscreen el panel no se arrastra: no hay a dónde moverlo.
  const handleDragStart = useCallback(( e: React.MouseEvent ) => {
    if ( !panelRef.current || isFullscreen ) return;
    e.preventDefault();

    // 1. Obtengo el rect actual
    const rect = panelRef.current.getBoundingClientRect();
    // 2. Calculo offset interno: coord click - left/top del panel
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    // 3. Cambio cursor y activo bandera
    document.body.style.cursor = 'grabbing';
    setIsDragging( true );
  }, [isFullscreen]);

  // Mientras muevo el mouse, actualizo panelPosition vía estado + requestAnimationFrame
  const handleDragMove = useCallback(
    ( e: MouseEvent ) => {
      if ( !isDragging || !panelRef.current ) return;

      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;

      // Limito a dentro de viewport
      const pw = panelRef.current.offsetWidth;
      const ph = panelRef.current.offsetHeight;
      newX = Math.max( 0, Math.min( newX, window.innerWidth - pw ));
      newY = Math.max( 0, Math.min( newY, window.innerHeight - ph ));

      requestAnimationFrame(() => {
        setPanelPosition({ x: newX, y: newY });
      });
    },
    [ isDragging, dragOffset ]
  );

  // Cuando suelto el mouse, desactivo dragging y restauro cursor
  const handleDragEnd = useCallback(() => {
    setIsDragging( false );
    document.body.style.cursor = '';
  }, []);

  useEffect(() => {
    if ( isDragging ) {
      document.addEventListener( 'mousemove', handleDragMove );
      document.addEventListener( 'mouseup', handleDragEnd );
    } else {
      document.removeEventListener( 'mousemove', handleDragMove );
      document.removeEventListener( 'mouseup', handleDragEnd );
    }
    return () => {
      document.removeEventListener( 'mousemove', handleDragMove );
      document.removeEventListener( 'mouseup', handleDragEnd );
    };
  }, [ isDragging, handleDragMove, handleDragEnd ]);

  // -----------------------------
  // 2) Lógica de minimizar / maximizar
  // -----------------------------

  const handleToggleMinimize = useCallback(() => {
    setIsMinimized( prev => !prev );
  }, []);

  // Este efecto se dispara cuando cambio `isMinimized`. En particular,
  // si paso de “minimizado” (true) a “no minimizado” (false), quiero
  // verificar si el panel actual desborda la pantalla en Y, y en tal
  // caso corregir `panelPosition.y`.
  useEffect(() => {
    // Si ahora está “desminimizado”:
    if ( !isMinimized && panelRef.current ) {
      // Espero un pequeño tick para que el panel se haya “resuelto” con su
      // contenido (por ejemplo, si antes estaba minimizado tal vez no tenía height completo).
      // Con setTimeout(..., 0) esperamos al próximo ciclo de event loop.
      setTimeout(() => {
        if ( !panelRef.current ) return;
        const rect = panelRef.current.getBoundingClientRect();
        const pw = rect.width;
        const ph = rect.height;

        // Si excede por abajo:
        if ( rect.bottom > window.innerHeight ) {
          // Quiero que quede justo en la parte inferior sin salirse:
          const correctedY = Math.max( 0, window.innerHeight - ph );
          setPanelPosition( pos => ({ x: pos.x, y: correctedY }));
        }
        // (Opcional) Si quieres, podrías también “corregir” X si desborda por la derecha:
        if ( rect.right > window.innerWidth ) {
          const correctedX = Math.max( 0, window.innerWidth - pw );
          setPanelPosition( pos => ({ x: correctedX, y: pos.y }));
        }
      }, 0 );
    }
  }, [isMinimized]);
  useEffect(() => {
    // Cada vez que isMinimized cambie (sea true→false ó false→true), ponemos animateTitle = true
    setAnimateTitle( true );

    // A los 1s (duración de la animación CSS) apagamos animateTitle
    const timeout = setTimeout(() => {
      setAnimateTitle( false );
    }, 1000 ); // coincide con “1s” en CSS

    return () => clearTimeout( timeout );
  }, [isMinimized]);
  // -----------------------------
  // 3) Renderizado
  // -----------------------------

  if ( !isVisible ) return null;

  // Decidimos si usar la posición “custom” (drag) o dejar que se ponga en el lugar por defecto de CSS
  const useCustomPosition = panelPosition.x !== 0 || panelPosition.y !== 0;

  // En fullscreen ignoramos por completo la posición de arrastre y el ancho
  // calculado: el tamaño lo fija la clase CSS contra `--app-navbar-height`.
  // Se anima con left/top/width/height (no con `inset`, que no interpola
  // contra `auto`).
  const panelStyle: React.CSSProperties = isFullscreen
    ? { zIndex: 1000 }
    : useCustomPosition
      ? {
          position: 'fixed',
          left: `${panelPosition.x}px`,
          top: `${panelPosition.y}px`,
          width: isMinimized ? '300px' : `${panelSize.width}px`,
          height: isMinimized ? 'auto' : undefined,
          zIndex: 1000
        }
      : {
          // Si no hay posición custom, delegamos en la clase CSS
          width: isMinimized ? '300px' : `${panelSize.width}px`,
          height: isMinimized ? 'auto' : undefined,
          zIndex: 1000
        };

  const nextMode: FloatingPanelMode = isFullscreen ? 'floating' : 'fullscreen';
  const modeLabel = isFullscreen
    ? ( modeLabels?.collapse ?? 'Ventana flotante' )
    : ( modeLabels?.expand ?? 'Pantalla completa' );

  return (
    <Box
      ref={panelRef}
      className={`${classes.floatingPanel} ${isFullscreen ? classes.fullscreen : ''} ${className || ''}`}
      style={panelStyle}
    >
      {/*
        `wrap="nowrap"` on BOTH groups is load-bearing. Mantine's `Group`
        defaults to `wrap="wrap"`, and the collapsed panel is only 300px wide —
        with the title plus three action icons the right group wrapped onto a
        second line and the title bar fell apart. The title truncates instead.
      */}
      <div className={classes.header}>
        <Group gap="xs" wrap="nowrap" className={classes.headerLeft}>
          {/* Arrastrar no tiene sentido en fullscreen — ocultamos el handle */}
          {!isFullscreen && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onMouseDown={handleDragStart}
              className={classes.dragHandle}
            >
              <IconGripVertical size={16} />
            </ActionIcon>
          )}
          <Text
            size="sm"
            fw={500}
            truncate="end"
            className={`${classes.headerTitle} ${animateTitle ? classes.shimmer : ''}`}
          >
            {title}
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {/* Collapsed to a title bar there is no room, and going fullscreen from
              a collapsed strip is not a move anyone makes — restore first. */}
          {onModeChange && !isMinimized && (
            <Tooltip label={modeLabel} zIndex={9999}>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={modeLabel}
                onClick={() => onModeChange( nextMode )}
              >
                {isFullscreen
                  ? <IconArrowsDiagonalMinimize2 size={16} />
                  : <IconArrowsDiagonal size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
          {/* Colapsar a barra de título: sólo aplica a la ventana flotante */}
          {!isFullscreen && (
            <ActionIcon variant="subtle" color="gray" onClick={handleToggleMinimize}>
              {isMinimized ? <IconMaximize size={16} /> : <IconMinimize size={16} />}
            </ActionIcon>
          )}
          {onClose && (
            <ActionIcon variant="subtle" color="gray" onClick={onClose}>
              <IconX size={16} />
            </ActionIcon>
          )}
        </Group>
      </div>
      {( isFullscreen || !isMinimized ) && <div className={classes.content}>{children}</div>}
    </Box>
  );
}

export default FloatingPanel;
